import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useTrendsOverview } from './hooks/useTrendsOverview';
import TrendArticleCard from './components/TrendArticleCard';
import TrendScore from './components/TrendScore';
import TrendHeroCard from './components/TrendHeroCard';
import TrendArticleDetailDialog from './components/TrendArticleDetailDialog';
import TrendStoryCard from './components/TrendStoryCard';
import TrendStoryDetailDialog from './components/TrendStoryDetailDialog';
import TrendOpportunityCard from './components/TrendOpportunityCard';
import TrendOpportunityDetailDialog from './components/TrendOpportunityDetailDialog';
import SourceEvidencePanel from './components/SourceEvidencePanel';
import type { TrendArticle, TrendCard, TrendOpportunity, TrendStory } from './types';
import {
  fetchTrendArticles,
  fetchTrendCards,
  fetchTrendOpportunities,
  fetchTrendStories,
  updateTrendReport,
} from './service';
import {
  getRunPresentationColor,
  getRunPresentationLabel,
  isLatestRunPresentation,
  sortByRunPresentationThenDate,
} from './runPresentationUtils';
import { matchesTrendChannel, useTrendChannel, TrendChannelFilter, type TrendChannelSource } from './channelFilter';
import { PAGE_HEADING_SX, SECTION_HEADING_SX, TREND_COLORS, DISPLAY_FONT } from './trendTheme';

const DEFAULT_OVERVIEW_SECTION_ORDER = [
  'hero',
  'weekly_highlights',
  'top_stories',
  'latest_articles',
  'top_opportunities',
] as const;

type OverviewSectionId = typeof DEFAULT_OVERVIEW_SECTION_ORDER[number];
type PickerTarget = 'hero' | 'weekly_highlights' | 'top_stories' | 'latest_articles' | 'top_opportunities';

type SortableAdminItem = {
  id: string;
  title: string;
  subtitle?: string;
  chipLabel?: string;
};

type PickerEntry = {
  id: string;
  title: string;
  subtitle?: string;
  chipLabel?: string;
  disabled?: boolean;
};

const SECTION_LABELS: Record<OverviewSectionId, string> = {
  hero: 'Huvudblocket',
  weekly_highlights: 'Det här präglar veckan',
  top_stories: 'Toppberättelser',
  latest_articles: 'Senaste artiklar',
  top_opportunities: 'Möjligheter för Fontana',
};

const SECTION_HELPERS: Record<OverviewSectionId, string> = {
  hero: 'Det stora toppblocket högst upp på översikten.',
  weekly_highlights: 'Artiklarna i sektionen "Det här präglar veckan".',
  top_stories: 'Berättelsekorten som visas under huvudblocket.',
  latest_articles: 'Senaste artikelkort på översikten.',
  top_opportunities: 'Möjligheter som visas längst ned på översikten.',
};

function EmptySection({ message }: { message: string }) {
  return (
    <Box
      sx={{
        py: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        color: TREND_COLORS.textMuted,
      }}
    >
      <SearchOffIcon sx={{ fontSize: 40, opacity: 0.5 }} />
      <Typography variant="body1" sx={{ fontStyle: 'italic' }}>{message}</Typography>
      <Typography variant="body2">
        Kör en trendspaning under Kontrollcenter för att fylla på med data.
      </Typography>
    </Box>
  );
}

function normalizeOverviewSectionOrder(values: unknown): OverviewSectionId[] {
  const explicit = Array.isArray(values)
    ? values.filter((entry): entry is OverviewSectionId => (
      entry === 'hero'
      || entry === 'weekly_highlights'
      || entry === 'top_stories'
      || entry === 'latest_articles'
      || entry === 'top_opportunities'
    ))
    : [];
  const seen = new Set<string>();
  const normalized: OverviewSectionId[] = [];

  [...explicit, ...DEFAULT_OVERVIEW_SECTION_ORDER].forEach((entry) => {
    if (seen.has(entry)) return;
    seen.add(entry);
    normalized.push(entry);
  });

  return normalized;
}

function mergeUniqueIds(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function mergeSavedIdsWithVisibleItems<T extends { _id: string }>(
  savedIds: string[] | undefined,
  visibleItems: T[],
): string[] {
  return mergeUniqueIds([
    ...(savedIds || []),
    ...visibleItems.map((item) => item._id),
  ]);
}

function getTrendCardQualityLabel(status: TrendCard['qualityStatus']): string {
  if (status === 'strong') return 'Starkt trendkort';
  if (status === 'needs_validation') return 'Kräver nordisk validering';
  if (status === 'inspiration_only') return 'Endast inspiration';
  if (status === 'weak') return 'Svagt underlag';
  return 'Möjlig trend';
}

function getTrendCardQualityColor(status: TrendCard['qualityStatus']): 'success' | 'warning' | 'default' {
  if (status === 'strong') return 'success';
  if (status === 'needs_validation' || status === 'inspiration_only') return 'warning';
  return 'default';
}

function mapItemsById<T extends { _id: string }>(items: Array<T | null | undefined>): Map<string, T> {
  const lookup = new Map<string, T>();
  items.forEach((item) => {
    if (!item?._id) return;
    lookup.set(item._id, item);
  });
  return lookup;
}

function resolveOrderedItems<T extends { _id: string }>(
  orderedIds: string[],
  lookup: Map<string, T>,
  fallbackItems: T[],
): T[] {
  const source = orderedIds.length > 0
    ? orderedIds
      .map((id) => lookup.get(id))
      .filter((item): item is T => Boolean(item))
    : fallbackItems;
  const seen = new Set<string>();
  return source.filter((item) => {
    if (!item?._id || seen.has(item._id)) return false;
    seen.add(item._id);
    return true;
  });
}

function buildSortableItems<T extends { _id: string; title?: string }>(
  ids: string[],
  lookup: Map<string, T>,
  builder: (item: T) => Omit<SortableAdminItem, 'id'>,
): SortableAdminItem[] {
  return ids.map((id) => {
    const item = lookup.get(id);
    if (!item) {
      return {
        id,
        title: `Objekt ${id.slice(-6)}`,
        subtitle: 'Kunde inte läsa metadata för detta objekt just nu.',
      };
    }
    return {
      id,
      ...builder(item),
    };
  });
}

function getPickerLabel(target: PickerTarget | null): string {
  if (target === 'weekly_highlights') return 'Lägg till i "Det här präglar veckan"';
  if (target === 'top_stories') return 'Lägg till i "Toppberättelser"';
  if (target === 'latest_articles') return 'Lägg till i "Senaste artiklar"';
  if (target === 'top_opportunities') return 'Lägg till i "Möjligheter för Fontana"';
  return 'Välj huvudartikel';
}

function reorderIds(ids: string[], activeId: string, overId: string): string[] {
  const oldIndex = ids.indexOf(activeId);
  const newIndex = ids.indexOf(overId);
  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return ids;
  return arrayMove(ids, oldIndex, newIndex);
}

function SortableAdminRow({
  item,
  onRemove,
}: {
  item: SortableAdminItem;
  onRemove?: (id: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={{
        borderColor: TREND_COLORS.divider,
        background: isDragging ? '#f8f6ef' : '#fff',
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 1 }}>
        <IconButton
          size="small"
          sx={{ color: TREND_COLORS.textMuted, cursor: isDragging ? 'grabbing' : 'grab' }}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ color: TREND_COLORS.textPrimary, fontWeight: 600 }}>
            {item.title}
          </Typography>
          {item.subtitle && (
            <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }} noWrap>
              {item.subtitle}
            </Typography>
          )}
        </Stack>
        {item.chipLabel && (
          <Chip label={item.chipLabel} size="small" sx={{ fontSize: '0.55rem', height: 20 }} />
        )}
        {onRemove && (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onRemove(item.id);
            }}
            sx={{ color: '#c0392b' }}
          >
            <RemoveCircleOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Paper>
  );
}

function SortableAdminList({
  title,
  helperText,
  items,
  emptyMessage,
  onReorder,
  onAdd,
  onRemove,
}: {
  title: string;
  helperText?: string;
  items: SortableAdminItem[];
  emptyMessage: string;
  onReorder: (nextIds: string[]) => void;
  onAdd?: () => void;
  onRemove?: (id: string) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const itemIds = items.map((item) => item.id);

  const handleDragEnd = (event: DragEndEvent) => {
    const activeId = String(event.active.id || '');
    const overId = event.over ? String(event.over.id || '') : '';
    if (!activeId || !overId || activeId === overId) return;
    onReorder(reorderIds(itemIds, activeId, overId));
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        borderColor: TREND_COLORS.divider,
        bgcolor: '#fff',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
          {title}
        </Typography>
        {onAdd && (
          <IconButton size="small" onClick={onAdd} sx={{ color: TREND_COLORS.navy }}>
            <AddIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      {helperText && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: TREND_COLORS.textMuted }}>
          {helperText}
        </Typography>
      )}
      {items.length === 0 ? (
        <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted, fontStyle: 'italic' }}>
          {emptyMessage}
        </Typography>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            <Stack spacing={1} sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {items.map((item) => (
                <SortableAdminRow key={item.id} item={item} onRemove={onRemove} />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
      )}
    </Paper>
  );
}

export default function TrendsOverviewPage({ canReview = false }: { canReview?: boolean }) {
  const { data, loading, error, reload } = useTrendsOverview();
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [selectedStoryId, setSelectedStoryId] = useState<string | null>(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState<TrendOpportunity | null>(null);
  const [selectedTrendCard, setSelectedTrendCard] = useState<TrendCard | null>(null);
  const [trendCards, setTrendCards] = useState<TrendCard[]>([]);
  const [newSinceOnly, setNewSinceOnly] = useState(false);

  const [adminMode, setAdminMode] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<PickerTarget | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerLoading, setPickerLoading] = useState(false);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminMsg, setAdminMsg] = useState<string | null>(null);

  const [allArticles, setAllArticles] = useState<TrendArticle[]>([]);
  const [allStories, setAllStories] = useState<TrendStory[]>([]);
  const [allOpportunities, setAllOpportunities] = useState<TrendOpportunity[]>([]);

  useEffect(() => {
    if (!canReview && adminMode) setAdminMode(false);
  }, [adminMode, canReview]);

  const [sectionOrder, setSectionOrder] = useState<OverviewSectionId[]>([...DEFAULT_OVERVIEW_SECTION_ORDER]);
  const [weeklyOrderIds, setWeeklyOrderIds] = useState<string[]>([]);
  const [topStoryOrderIds, setTopStoryOrderIds] = useState<string[]>([]);
  const [latestOrderIds, setLatestOrderIds] = useState<string[]>([]);
  const [opportunityOrderIds, setOpportunityOrderIds] = useState<string[]>([]);
  const [heroArticleId, setHeroArticleId] = useState<string>('');

  const activeReport = data?.report || null;
  const activeBriefing = data?.briefing || null;

  const topSignals = useMemo(() => (Array.isArray(data?.topSignals) ? [...(data?.topSignals || [])] : []), [data]);
  const weeklyHighlights = useMemo(() => (Array.isArray(data?.weeklyHighlights) ? [...(data?.weeklyHighlights || [])] : []), [data]);
  const topStories = useMemo(() => (
    Array.isArray(data?.topStories)
      ? (data?.topStories || []).filter((story) => story._id !== data?.heroStory?._id)
      : []
  ), [data]);
  const latestArticles = useMemo(() => (Array.isArray(data?.latestArticles) ? [...(data?.latestArticles || [])] : []), [data]);
  const topOpportunities = useMemo(() => (Array.isArray(data?.topOpportunities) ? [...(data?.topOpportunities || [])] : []), [data]);

  const articleLookup = useMemo(() => mapItemsById<TrendArticle>([
    ...allArticles,
    ...weeklyHighlights,
    ...latestArticles,
    data?.heroArticle || undefined,
  ]), [allArticles, weeklyHighlights, latestArticles, data]);

  const storyLookup = useMemo(() => mapItemsById<TrendStory>([
    ...allStories,
    ...topStories,
    data?.heroStory || undefined,
  ]), [allStories, topStories, data]);

  const opportunityLookup = useMemo(() => mapItemsById<TrendOpportunity>([
    ...allOpportunities,
    ...topOpportunities,
  ]), [allOpportunities, topOpportunities]);

  useEffect(() => {
    if (!data) return;
    setSectionOrder(normalizeOverviewSectionOrder(
      activeReport?.overviewManualMode
        ? activeReport.sectionOrder
        : DEFAULT_OVERVIEW_SECTION_ORDER,
    ));
    setWeeklyOrderIds(mergeSavedIdsWithVisibleItems(activeReport?.pinnedWeeklyArticleIds, weeklyHighlights));
    setTopStoryOrderIds(mergeSavedIdsWithVisibleItems(activeReport?.pinnedTopStoryIds, topStories));
    setLatestOrderIds(mergeSavedIdsWithVisibleItems(activeReport?.pinnedLatestArticleIds, latestArticles));
    setOpportunityOrderIds(mergeSavedIdsWithVisibleItems(activeReport?.pinnedOpportunityIds, topOpportunities));
    setHeroArticleId(activeReport?.heroArticleId || '');
  }, [activeReport, data, latestArticles, topOpportunities, topStories, weeklyHighlights]);

  useEffect(() => {
    if (!adminMode) return undefined;

    let cancelled = false;

    void (async () => {
      const tasks: Array<Promise<void>> = [];

      if (allArticles.length === 0) {
        tasks.push(fetchTrendArticles().then((items) => {
          if (!cancelled) setAllArticles(items);
        }));
      }
      if (allStories.length === 0) {
        tasks.push(fetchTrendStories().then((items) => {
          if (!cancelled) setAllStories(items);
        }));
      }
      if (allOpportunities.length === 0) {
        tasks.push(fetchTrendOpportunities().then((items) => {
          if (!cancelled) setAllOpportunities(items);
        }));
      }

      if (tasks.length > 0) {
        await Promise.allSettled(tasks);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adminMode, allArticles.length, allOpportunities.length, allStories.length]);

  useEffect(() => {
    let cancelled = false;
    void fetchTrendCards({ limit: 6 })
      .then((cards) => {
        if (!cancelled) setTrendCards(sortByRunPresentationThenDate(cards).slice(0, 6));
      })
      .catch(() => {
        if (!cancelled) setTrendCards([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openPicker = useCallback(async (target: PickerTarget) => {
    setPickerTarget(target);
    setPickerSearch('');
    setPickerOpen(true);
    setPickerLoading(true);
    try {
      if ((target === 'hero' || target === 'weekly_highlights' || target === 'latest_articles') && allArticles.length === 0) {
        setAllArticles(await fetchTrendArticles());
      }
      if (target === 'top_stories' && allStories.length === 0) {
        setAllStories(await fetchTrendStories());
      }
      if (target === 'top_opportunities' && allOpportunities.length === 0) {
        setAllOpportunities(await fetchTrendOpportunities());
      }
    } finally {
      setPickerLoading(false);
    }
  }, [allArticles.length, allOpportunities.length, allStories.length]);

  const handlePickerSelect = (id: string) => {
    if (pickerTarget === 'hero') {
      setHeroArticleId(id);
    } else if (pickerTarget === 'weekly_highlights') {
      setWeeklyOrderIds((prev) => mergeUniqueIds([...prev, id]));
    } else if (pickerTarget === 'top_stories') {
      setTopStoryOrderIds((prev) => mergeUniqueIds([...prev, id]));
    } else if (pickerTarget === 'latest_articles') {
      setLatestOrderIds((prev) => mergeUniqueIds([...prev, id]));
    } else if (pickerTarget === 'top_opportunities') {
      setOpportunityOrderIds((prev) => mergeUniqueIds([...prev, id]));
    }
    setPickerOpen(false);
  };

  const previewWeeklyHighlights = useMemo(
    () => resolveOrderedItems(weeklyOrderIds, articleLookup, weeklyHighlights),
    [weeklyOrderIds, articleLookup, weeklyHighlights],
  );
  const previewTopStories = useMemo(
    () => resolveOrderedItems(topStoryOrderIds, storyLookup, topStories),
    [topStoryOrderIds, storyLookup, topStories],
  );
  const previewLatestArticles = useMemo(
    () => resolveOrderedItems(latestOrderIds, articleLookup, latestArticles),
    [latestOrderIds, articleLookup, latestArticles],
  );
  const previewTopOpportunities = useMemo(
    () => resolveOrderedItems(opportunityOrderIds, opportunityLookup, topOpportunities),
    [opportunityOrderIds, opportunityLookup, topOpportunities],
  );
  const previewHeroArticle = heroArticleId
    ? articleLookup.get(heroArticleId) || (data?.heroArticle?._id === heroArticleId ? data.heroArticle : null)
    : data?.heroArticle || null;
  const previewHeroStory = heroArticleId ? null : (data?.heroStory || null);
  const { channel } = useTrendChannel();

  const readerNewSinceFilter = useCallback(
    <T extends { runPresentation?: TrendArticle['runPresentation'] }>(items: T[]): T[] => (
      items.filter((item) => (
        (!newSinceOnly || isLatestRunPresentation(item.runPresentation))
        && matchesTrendChannel(item as TrendChannelSource, channel)
      ))
    ),
    [newSinceOnly, channel],
  );

  const renderedHeroArticle = adminMode ? previewHeroArticle : (data?.heroArticle || null);
  const renderedHeroStory = adminMode ? previewHeroStory : (data?.heroStory || null);
  const renderedWeeklyHighlights = adminMode ? previewWeeklyHighlights : readerNewSinceFilter(weeklyHighlights);
  const renderedTopStories = adminMode ? previewTopStories : topStories;
  const renderedLatestArticles = adminMode ? previewLatestArticles : readerNewSinceFilter(latestArticles);
  const renderedTopOpportunities = adminMode ? previewTopOpportunities : readerNewSinceFilter(topOpportunities);
  const renderedTrendCards = adminMode ? trendCards : readerNewSinceFilter(trendCards);

  const newSinceCounts = useMemo(() => {
    const isNew = (value?: TrendArticle['runPresentation']) => isLatestRunPresentation(value);
    const uniqueArticles = mapItemsById<TrendArticle>([
      ...weeklyHighlights,
      ...latestArticles,
      data?.heroArticle || undefined,
    ]);
    const articles = Array.from(uniqueArticles.values()).filter((article) => isNew(article.runPresentation)).length;
    const opportunities = topOpportunities.filter((item) => isNew(item.runPresentation)).length;
    const cards = trendCards.filter((card) => isNew(card.runPresentation)).length;
    return {
      articles,
      opportunities,
      cards,
      total: articles + opportunities + cards,
    };
  }, [weeklyHighlights, latestArticles, topOpportunities, trendCards, data]);

  const sectionOrderItems = useMemo<SortableAdminItem[]>(
    () => normalizeOverviewSectionOrder(sectionOrder).map((sectionId) => ({
      id: sectionId,
      title: SECTION_LABELS[sectionId],
      subtitle: SECTION_HELPERS[sectionId],
    })),
    [sectionOrder],
  );

  const weeklyAdminItems = useMemo(
    () => buildSortableItems(weeklyOrderIds, articleLookup, (article) => ({
      title: article.title,
      subtitle: article.excerpt || article.whyItMatters || article.sourceReferences?.[0]?.sourceName || '',
      chipLabel: String(article.category || '').toUpperCase(),
    })),
    [weeklyOrderIds, articleLookup],
  );
  const topStoryAdminItems = useMemo(
    () => buildSortableItems(topStoryOrderIds, storyLookup, (story) => ({
      title: story.title,
      subtitle: story.summary || story.whatItMeansForUs || '',
      chipLabel: String(story.category || '').toUpperCase(),
    })),
    [topStoryOrderIds, storyLookup],
  );
  const latestAdminItems = useMemo(
    () => buildSortableItems(latestOrderIds, articleLookup, (article) => ({
      title: article.title,
      subtitle: article.excerpt || article.whyItMatters || article.sourceReferences?.[0]?.sourceName || '',
      chipLabel: String(article.category || '').toUpperCase(),
    })),
    [latestOrderIds, articleLookup],
  );
  const opportunityAdminItems = useMemo(
    () => buildSortableItems(opportunityOrderIds, opportunityLookup, (opportunity) => ({
      title: opportunity.title,
      subtitle: opportunity.summary || opportunity.whyRelevant || '',
      chipLabel: String(opportunity.type || '').replace(/_/g, ' '),
    })),
    [opportunityOrderIds, opportunityLookup],
  );

  const pickerEntries = useMemo<PickerEntry[]>(() => {
    const query = pickerSearch.trim().toLowerCase();
    if (pickerTarget === 'top_stories') {
      const selectedIds = new Set(topStoryOrderIds);
      return allStories
        .filter((story) => !query || story.title.toLowerCase().includes(query))
        .slice(0, 80)
        .map((story) => ({
          id: story._id,
          title: story.title,
          subtitle: story.summary || story.whatItMeansForUs || '',
          chipLabel: String(story.category || '').toUpperCase(),
          disabled: selectedIds.has(story._id),
        }));
    }
    if (pickerTarget === 'top_opportunities') {
      const selectedIds = new Set(opportunityOrderIds);
      return allOpportunities
        .filter((opportunity) => !query || opportunity.title.toLowerCase().includes(query))
        .slice(0, 80)
        .map((opportunity) => ({
          id: opportunity._id,
          title: opportunity.title,
          subtitle: opportunity.summary || opportunity.whyRelevant || '',
          chipLabel: String(opportunity.type || '').replace(/_/g, ' '),
          disabled: selectedIds.has(opportunity._id),
        }));
    }
    const selectedIds = new Set(
      pickerTarget === 'weekly_highlights'
        ? weeklyOrderIds
        : pickerTarget === 'latest_articles'
          ? latestOrderIds
          : [],
    );
    return allArticles
      .filter((article) => !query || article.title.toLowerCase().includes(query))
      .slice(0, 80)
      .map((article) => ({
        id: article._id,
        title: article.title,
        subtitle: article.excerpt || article.whyItMatters || article.sourceReferences?.[0]?.sourceName || '',
        chipLabel: String(article.category || '').toUpperCase(),
        disabled: pickerTarget !== 'hero' && selectedIds.has(article._id),
      }));
  }, [
    allArticles,
    allOpportunities,
    allStories,
    latestOrderIds,
    opportunityOrderIds,
    pickerSearch,
    pickerTarget,
    topStoryOrderIds,
    weeklyOrderIds,
  ]);

  const handleAdminSave = async () => {
    if (!activeReport?._id) return;
    setAdminSaving(true);
    try {
      await updateTrendReport(activeReport._id, {
        overviewManualMode: true,
        sectionOrder: normalizeOverviewSectionOrder(sectionOrder),
        heroArticleId: heroArticleId || null,
        heroStoryId: null,
        pinnedWeeklyArticleIds: weeklyOrderIds,
        pinnedTopStoryIds: topStoryOrderIds,
        pinnedLatestArticleIds: latestOrderIds,
        pinnedOpportunityIds: opportunityOrderIds,
      } as any);
      setAdminMsg('Overview-layout sparad.');
      await reload();
      setTimeout(() => setAdminMsg(null), 3000);
    } catch {
      setAdminMsg('Kunde inte spara overview-layouten.');
    } finally {
      setAdminSaving(false);
    }
  };

  const handleResetAutoLayout = async () => {
    if (!activeReport?._id) return;
    setAdminSaving(true);
    try {
      await updateTrendReport(activeReport._id, {
        overviewManualMode: false,
        sectionOrder: [...DEFAULT_OVERVIEW_SECTION_ORDER],
        heroArticleId: null,
        heroStoryId: null,
        pinnedWeeklyArticleIds: [],
        pinnedTopStoryIds: [],
        pinnedLatestArticleIds: [],
        pinnedOpportunityIds: [],
      } as any);
      setAdminMsg('Overviewn återställd till automatiskt urval.');
      await reload();
      setTimeout(() => setAdminMsg(null), 3000);
    } catch {
      setAdminMsg('Kunde inte återställa overviewn.');
    } finally {
      setAdminSaving(false);
    }
  };

  const weekNumber = Math.ceil((new Date().getTime() - new Date(new Date().getFullYear(), 0, 1).getTime()) / 604800000);
  const hasAnyData = renderedTopStories.length > 0
    || renderedLatestArticles.length > 0
    || renderedWeeklyHighlights.length > 0
    || topSignals.length > 0
    || renderedTopOpportunities.length > 0
    || trendCards.length > 0
    || !!activeBriefing
    || renderedHeroArticle
    || renderedHeroStory;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress sx={{ color: TREND_COLORS.navy }} />
      </Box>
    );
  }

  if (error || !data) {
    return <Alert severity="error">{error || 'Kunde inte ladda trendöversikten.'}</Alert>;
  }

  const renderedSections: Record<OverviewSectionId, React.ReactNode> = {
    hero: (
      <TrendHeroCard
        report={data.report}
        story={renderedHeroStory}
        article={renderedHeroArticle}
        signals={topSignals}
        onReadMore={(article) => setSelectedArticleId(article._id)}
        onReadMoreStory={(story) => setSelectedStoryId(story._id)}
      />
    ),
    weekly_highlights: (
      <Stack spacing={2.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={SECTION_HEADING_SX}>
              DET HÄR PRÄGLAR VECKAN
            </Typography>
            {adminMode && (
              <IconButton size="small" onClick={() => void openPicker('weekly_highlights')} sx={{ color: TREND_COLORS.navy }}>
                <AddIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>
        {renderedWeeklyHighlights.length === 0 ? (
          <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted, fontStyle: 'italic' }}>
            Inga artiklar har genererats ännu.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {renderedWeeklyHighlights.map((article, idx) => (
              <Grid key={article._id} size={{ xs: 12, sm: idx < 2 ? 6 : 6, lg: idx < 2 ? 6 : 3 }}>
                <Box sx={{ position: 'relative' }}>
                  <TrendArticleCard article={article} onReadMore={(entry) => setSelectedArticleId(entry._id)} />
                  {adminMode && (
                    <IconButton
                      size="small"
                      onClick={() => setWeeklyOrderIds((prev) => prev.filter((id) => id !== article._id))}
                      sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#fde8e8' } }}
                    >
                      <RemoveCircleOutlineIcon fontSize="small" sx={{ color: '#c0392b' }} />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    ),
    top_stories: (
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={SECTION_HEADING_SX}>
            TOPPBERÄTTELSER
          </Typography>
          {adminMode && (
            <IconButton size="small" onClick={() => void openPicker('top_stories')} sx={{ color: TREND_COLORS.navy }}>
              <AddIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        {renderedTopStories.length === 0 ? (
          <EmptySection message={renderedHeroStory ? 'Huvudberättelsen visas redan överst. Fler berättelser fylls på när underlaget blir bredare.' : 'Berättelselagret är ännu inte byggt för denna trendspaning.'} />
        ) : (
          <Grid container spacing={3}>
            {renderedTopStories.map((story, idx) => (
              <Grid key={story._id} size={{ xs: 12, sm: idx === 0 ? 12 : 6, lg: idx === 0 ? 8 : 4 }}>
                <Box sx={{ position: 'relative' }}>
                  <TrendStoryCard story={story} onOpen={(entry) => setSelectedStoryId(entry._id)} />
                  {adminMode && (
                    <IconButton
                      size="small"
                      onClick={() => setTopStoryOrderIds((prev) => prev.filter((id) => id !== story._id))}
                      sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#fde8e8' } }}
                    >
                      <RemoveCircleOutlineIcon fontSize="small" sx={{ color: '#c0392b' }} />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    ),
    latest_articles: (
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={SECTION_HEADING_SX}>
            SENASTE ARTIKLAR
          </Typography>
          {adminMode && (
            <IconButton size="small" onClick={() => void openPicker('latest_articles')} sx={{ color: TREND_COLORS.navy }}>
              <AddIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        {renderedLatestArticles.length === 0 ? (
          <EmptySection message="Inga artiklar har genererats ännu." />
        ) : (
          <Grid container spacing={3}>
            {renderedLatestArticles.map((article) => (
              <Grid key={article._id} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Box sx={{ position: 'relative' }}>
                  <TrendArticleCard article={article} onReadMore={(entry) => setSelectedArticleId(entry._id)} />
                  {adminMode && (
                    <IconButton
                      size="small"
                      onClick={() => setLatestOrderIds((prev) => prev.filter((id) => id !== article._id))}
                      sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#fde8e8' } }}
                    >
                      <RemoveCircleOutlineIcon fontSize="small" sx={{ color: '#c0392b' }} />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    ),
    top_opportunities: (
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={SECTION_HEADING_SX}>
            MÖJLIGHETER FÖR FONTANA
          </Typography>
          {adminMode && (
            <>
              <IconButton size="small" onClick={() => void openPicker('top_opportunities')} sx={{ color: TREND_COLORS.navy }}>
                <AddIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                {renderedTopOpportunities.length} visas
              </Typography>
            </>
          )}
        </Stack>
        {renderedTopOpportunities.length === 0 ? (
          <EmptySection message="Inga möjligheter identifierade ännu." />
        ) : (
          <Grid container spacing={3}>
            {renderedTopOpportunities.map((opportunity) => (
              <Grid key={opportunity._id} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Box sx={{ position: 'relative' }}>
                  <TrendOpportunityCard opportunity={opportunity} onOpen={(entry) => setSelectedOpportunity(entry)} />
                  {adminMode && (
                    <IconButton
                      size="small"
                      onClick={() => setOpportunityOrderIds((prev) => prev.filter((id) => id !== opportunity._id))}
                      sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#fde8e8' } }}
                    >
                      <RemoveCircleOutlineIcon fontSize="small" sx={{ color: '#c0392b' }} />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    ),
  };

  return (
    <Stack spacing={5} sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <div>
            <Typography variant="overline" sx={{ color: TREND_COLORS.textMuted, letterSpacing: '0.2em', fontWeight: 600 }}>
              Vecka {weekNumber} · {new Date().getFullYear()}
            </Typography>
            <Typography sx={PAGE_HEADING_SX}>
              TRENDSPANING
            </Typography>
          </div>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {!adminMode && <TrendChannelFilter minWidth={160} />}
            {canReview && (
              <Tooltip title={adminMode ? 'Stäng admin-läge' : 'Admin-läge'}>
                <IconButton onClick={() => setAdminMode((prev) => !prev)} sx={{ color: adminMode ? TREND_COLORS.gold : TREND_COLORS.textMuted }}>
                  {adminMode ? <CloseIcon /> : <SettingsIcon />}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        {adminMode && (
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderColor: TREND_COLORS.divider,
              bgcolor: TREND_COLORS.cardBg,
            }}
          >
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1.5}
              >
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={activeReport?.overviewManualMode ? 'MANUELL ÖVERSIKTSLAYOUT AKTIV' : 'AUTOMATISKT URVAL AKTIVT'}
                      size="small"
                      sx={{ bgcolor: TREND_COLORS.gold, color: TREND_COLORS.navy, fontWeight: 700, fontSize: '0.7rem' }}
                    />
                    {adminMsg && (
                      <Typography variant="caption" sx={{ color: TREND_COLORS.olive }}>
                        {adminMsg}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary }}>
                    Dra för att ändra ordningen mellan översiktens sektioner och innehåll. Tom lista betyder automatiskt urval för just den sektionen.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    disabled={adminSaving || !activeReport?._id}
                    onClick={handleResetAutoLayout}
                    sx={{ borderColor: TREND_COLORS.divider, color: TREND_COLORS.navy }}
                  >
                    Återställ auto
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={adminSaving || !activeReport?._id}
                    onClick={handleAdminSave}
                    sx={{ bgcolor: TREND_COLORS.navy, fontSize: '0.75rem' }}
                  >
                    {adminSaving ? 'Sparar...' : 'Spara overview-layout'}
                  </Button>
                </Stack>
              </Stack>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SortableAdminList
                    title="Sektionernas ordning"
                    helperText="Det här styr i vilken ordning blocken visas på overviewn."
                    items={sectionOrderItems}
                    emptyMessage="Inga sektioner att sortera."
                    onReorder={(nextIds) => setSectionOrder(normalizeOverviewSectionOrder(nextIds))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', borderColor: TREND_COLORS.divider }}>
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
                        Hero-blocket
                      </Typography>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                        Välj en huvudartikel om du vill att översikten ska öppna med en artikel i stället för automatiskt vald berättelse.
                      </Typography>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          border: `1px solid ${TREND_COLORS.divider}`,
                          bgcolor: '#fff',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: TREND_COLORS.textPrimary }}>
                          {heroArticleId
                            ? (previewHeroArticle?.title || 'Vald huvudartikel kunde inte laddas ännu')
                            : (data.heroArticle?.title || 'Automatiskt huvudval')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                          {heroArticleId
                            ? 'Manuellt vald huvudartikel'
                            : (previewHeroStory?.title
                              ? `Automatisk berättelse: ${previewHeroStory.title}`
                              : 'Systemet använder automatiskt huvudval när du inte väljer en artikel.')}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => void openPicker('hero')}
                          sx={{ borderColor: TREND_COLORS.divider, color: TREND_COLORS.navy }}
                        >
                          Välj artikel
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          disabled={!heroArticleId}
                          onClick={() => setHeroArticleId('')}
                          sx={{ color: TREND_COLORS.textMuted }}
                        >
                          Rensa huvudartikel
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SortableAdminList
                    title='Det här präglar veckan'
                    helperText='Sortera eller lägg till artiklar. Om listan töms går sektionen tillbaka till automatiskt urval.'
                    items={weeklyAdminItems}
                    emptyMessage='Inga artiklar valda just nu.'
                    onAdd={() => void openPicker('weekly_highlights')}
                    onRemove={(id) => setWeeklyOrderIds((prev) => prev.filter((entry) => entry !== id))}
                    onReorder={setWeeklyOrderIds}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SortableAdminList
                    title='Toppberättelser'
                    helperText='Sortera eller lägg till berättelser. Tom lista betyder automatiskt berättelseurval.'
                    items={topStoryAdminItems}
                    emptyMessage='Inga berättelser valda just nu.'
                    onAdd={() => void openPicker('top_stories')}
                    onRemove={(id) => setTopStoryOrderIds((prev) => prev.filter((entry) => entry !== id))}
                    onReorder={setTopStoryOrderIds}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SortableAdminList
                    title='Senaste artiklar'
                    helperText='Sortera eller lägg till artiklar. Tom lista betyder automatiskt urval.'
                    items={latestAdminItems}
                    emptyMessage='Inga artiklar valda just nu.'
                    onAdd={() => void openPicker('latest_articles')}
                    onRemove={(id) => setLatestOrderIds((prev) => prev.filter((entry) => entry !== id))}
                    onReorder={setLatestOrderIds}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SortableAdminList
                    title='Möjligheter för Fontana'
                    helperText='Sortera eller lägg till möjligheter. Tom lista betyder automatiskt urval.'
                    items={opportunityAdminItems}
                    emptyMessage='Inga möjligheter valda just nu.'
                    onAdd={() => void openPicker('top_opportunities')}
                    onRemove={(id) => setOpportunityOrderIds((prev) => prev.filter((entry) => entry !== id))}
                    onReorder={setOpportunityOrderIds}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>
        )}
      </Stack>

      {!adminMode && newSinceCounts.total > 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: 3,
            borderColor: TREND_COLORS.divider,
            bgcolor: TREND_COLORS.cardBg,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <Stack spacing={0.5}>
              <Typography variant="overline" sx={{ color: TREND_COLORS.textMuted, letterSpacing: '0.16em', fontWeight: 700 }}>
                Nytt sedan senaste körningen
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip size="small" color="success" label={`${newSinceCounts.total} nytt totalt`} />
                {newSinceCounts.articles > 0 && (
                  <Chip size="small" variant="outlined" label={`${newSinceCounts.articles} artiklar`} />
                )}
                {newSinceCounts.opportunities > 0 && (
                  <Chip size="small" variant="outlined" label={`${newSinceCounts.opportunities} möjligheter`} />
                )}
                {newSinceCounts.cards > 0 && (
                  <Chip size="small" variant="outlined" label={`${newSinceCounts.cards} trendkort`} />
                )}
              </Stack>
            </Stack>
            <Button
              size="small"
              variant={newSinceOnly ? 'contained' : 'outlined'}
              onClick={() => setNewSinceOnly((prev) => !prev)}
              sx={newSinceOnly
                ? { bgcolor: TREND_COLORS.navy, fontSize: '0.75rem' }
                : { borderColor: TREND_COLORS.divider, color: TREND_COLORS.navy, fontSize: '0.75rem' }}
            >
              {newSinceOnly ? 'Visar bara nytt' : 'Visa bara nytt sedan senaste körningen'}
            </Button>
          </Stack>
        </Paper>
      )}

      {activeBriefing && (
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            bgcolor: TREND_COLORS.navy,
            color: '#fff',
            boxShadow: '0 18px 50px rgba(7,47,83,0.18)',
          }}
        >
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
              <Stack spacing={0.75}>
                <Typography variant="overline" sx={{ color: TREND_COLORS.gold, letterSpacing: '0.18em', fontWeight: 800 }}>
                  Intelligence Briefing
                </Typography>
                <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: { xs: '1.6rem', md: '2.1rem' }, lineHeight: 1.1 }}>
                  {activeBriefing.title}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.78)', maxWidth: 760, lineHeight: 1.7 }}>
                  {activeBriefing.executiveSummary}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ alignContent: 'flex-start' }}>
                <Chip size="small" label={activeBriefing.status} sx={{ bgcolor: TREND_COLORS.gold, color: TREND_COLORS.navy, fontWeight: 800 }} />
                <Chip size="small" label={`${activeBriefing.topTrendCardIds.length} trendkort`} sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff' }} />
                <Chip size="small" label={`${activeBriefing.sourceSummary.primarySourceCount} A/B-källor`} sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff' }} />
              </Stack>
            </Stack>
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Paper sx={{ p: 2.5, bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)' }}>
                  <Typography variant="subtitle2" sx={{ color: TREND_COLORS.gold, fontWeight: 800, mb: 1 }}>
                    Huvudslutsats
                  </Typography>
                  <Typography sx={{ color: '#fff', lineHeight: 1.75 }}>
                    {activeBriefing.mainConclusion}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Paper sx={{ p: 2.5, bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)' }}>
                  <Typography variant="subtitle2" sx={{ color: TREND_COLORS.gold, fontWeight: 800, mb: 1 }}>
                    Nästa åtgärder
                  </Typography>
                  <Stack component="ul" spacing={0.75} sx={{ pl: 2, my: 0 }}>
                    {activeBriefing.recommendedNextActions.slice(0, 4).map((entry, index) => (
                      <Typography key={`${entry.department}-${index}`} component="li" variant="body2" sx={{ color: 'rgba(255,255,255,0.86)', lineHeight: 1.55 }}>
                        {entry.department}: {entry.action}
                      </Typography>
                    ))}
                    {activeBriefing.recommendedNextActions.length === 0 && (
                      <Typography component="li" variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                        Inga åtgärder ännu. Kör en bredare testkörning eller standardkörning.
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
            {activeBriefing.departmentBriefs.length > 0 && (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2" sx={{ color: TREND_COLORS.gold, fontWeight: 800 }}>
                    Avdelningsbriefar
                  </Typography>
                  <Chip
                    size="small"
                    label={`${activeBriefing.departmentBriefs.length} avdelningar`}
                    sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff' }}
                  />
                </Stack>
                <Grid container spacing={1.5}>
                  {activeBriefing.departmentBriefs.map((brief) => (
                    <Grid key={brief.department} size={{ xs: 12, sm: 6, md: 3 }}>
                      <Paper
                        sx={{
                          p: 1.75,
                          height: '100%',
                          bgcolor: 'rgba(255,255,255,0.08)',
                          border: '1px solid rgba(255,255,255,0.14)',
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={0.75} justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 800 }}>
                              {brief.label || brief.department}
                            </Typography>
                            <Chip
                              size="small"
                              label={`${brief.sourceEvidenceRefs.length} källor`}
                              sx={{ height: 20, bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.82)' }}
                            />
                          </Stack>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.55 }}>
                            {brief.summary}
                          </Typography>
                          {brief.recommendedActions[0] && (
                            <Typography variant="caption" sx={{ color: TREND_COLORS.gold, lineHeight: 1.45 }}>
                              Action: {brief.recommendedActions[0].action}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            <Chip
                              size="small"
                              label={`${brief.relevantTrendCardIds.length} trendkort`}
                              sx={{ height: 20, bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.78)' }}
                            />
                            <Chip
                              size="small"
                              label={`${brief.relevantOpportunityIds.length} möjligheter`}
                              sx={{ height: 20, bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.78)' }}
                            />
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            )}
            {activeBriefing.risksAndUncertainties.length > 0 && (
              <Alert severity="warning" sx={{ bgcolor: 'rgba(255,244,214,0.95)' }}>
                {activeBriefing.risksAndUncertainties.slice(0, 3).join(' ')}
              </Alert>
            )}
            <SourceEvidencePanel
              refs={activeBriefing.sourceEvidenceRefs}
              collectedButUnusedCount={activeBriefing.collectedButUnusedSourceItemIds.length}
              title="Källor bakom briefingen"
              dense
              dark
            />
          </Stack>
        </Paper>
      )}

      {!hasAnyData ? (
        <Box
          sx={{
            bgcolor: TREND_COLORS.cardBg,
            borderRadius: 3,
            border: `1px solid ${TREND_COLORS.divider}`,
            p: { xs: 4, md: 6 },
            textAlign: 'center',
          }}
        >
          <SearchOffIcon sx={{ fontSize: 56, color: TREND_COLORS.textMuted, opacity: 0.4, mb: 2 }} />
          <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.4rem', color: TREND_COLORS.navy, mb: 1 }}>
            Ingen trenddata ännu
          </Typography>
          <Typography variant="body1" sx={{ color: TREND_COLORS.textSecondary, maxWidth: 480, mx: 'auto' }}>
            Gå till Kontrollcenter, kontrollera att det finns aktiva källor och klicka på "Kör nu" för att starta din första trendspaning.
          </Typography>
        </Box>
      ) : (
        normalizeOverviewSectionOrder(sectionOrder).map((sectionId, index, sections) => (
          <React.Fragment key={sectionId}>
            {renderedSections[sectionId]}
            {index < sections.length - 1 && (
              <Divider sx={{ borderColor: TREND_COLORS.divider }} />
            )}
          </React.Fragment>
        ))
      )}

      {renderedTrendCards.length > 0 && (
        <Stack spacing={2.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Stack spacing={0.25}>
              <Typography sx={SECTION_HEADING_SX}>
                TRENDKORT V2
              </Typography>
              <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted }}>
                Källspårbara huvudkort med evidens, geografi, scores och rekommenderad action.
              </Typography>
            </Stack>
          </Stack>
          <Grid container spacing={3}>
            {renderedTrendCards.map((card) => (
              <Grid key={card._id} size={{ xs: 12, md: 6, lg: 4 }}>
                <Paper
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedTrendCard(card)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') setSelectedTrendCard(card);
                  }}
                  sx={{
                    height: '100%',
                    p: 2.5,
                    borderRadius: 3,
                    border: `1px solid ${TREND_COLORS.divider}`,
                    bgcolor: TREND_COLORS.cardBg,
                    cursor: 'pointer',
                    '&:hover': { boxShadow: '0 8px 26px rgba(7,47,83,0.10)', transform: 'translateY(-2px)' },
                    transition: 'all 160ms ease',
                  }}
                >
                  <Stack spacing={1.5} sx={{ height: '100%' }}>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        color={getTrendCardQualityColor(card.qualityStatus)}
                        label={getTrendCardQualityLabel(card.qualityStatus)}
                      />
                      {isLatestRunPresentation(card.runPresentation) && (
                        <Chip
                          size="small"
                          color={getRunPresentationColor(card.runPresentation)}
                          label={getRunPresentationLabel(card.runPresentation)}
                        />
                      )}
                      <TrendScore label="Prioritet" value={card.scores.overallPriority} />
                      <Chip size="small" variant="outlined" label={card.status} />
                    </Stack>
                    <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, color: TREND_COLORS.navy, fontSize: '1.1rem', lineHeight: 1.25 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7, flex: 1 }}>
                      {card.shortSummary || card.fontanaRelevance}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {card.lanes.slice(0, 3).map((lane) => (
                        <Chip key={lane} size="small" label={lane} sx={{ fontSize: '0.65rem' }} />
                      ))}
                    </Stack>
                    <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                      Evidens {card.evidence.evidenceStrength}/100 · {card.evidence.sourceCount} källor · Nivå A/B {card.evidence.sourceTierCounts.A + card.evidence.sourceTierCounts.B}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Stack>
      )}

      <Dialog open={!!selectedTrendCard} onClose={() => setSelectedTrendCard(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: DISPLAY_FONT, color: TREND_COLORS.navy }}>
          {selectedTrendCard?.title || 'Trendkort'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedTrendCard && (
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip color={getTrendCardQualityColor(selectedTrendCard.qualityStatus)} label={getTrendCardQualityLabel(selectedTrendCard.qualityStatus)} />
                <Chip variant="outlined" label={`Prioritet ${selectedTrendCard.scores.overallPriority}/100`} />
                <Chip variant="outlined" label={`Evidens ${selectedTrendCard.evidence.evidenceStrength}/100`} />
                <Chip variant="outlined" label={`Källor ${selectedTrendCard.evidence.sourceCount}`} />
                <Chip variant="outlined" label={`Geografi ${selectedTrendCard.evidence.primaryGeography}`} />
              </Stack>
              <Typography variant="body1" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.8 }}>
                {selectedTrendCard.detailedAnalysis || selectedTrendCard.shortSummary}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderColor: TREND_COLORS.divider }}>
                    <Typography variant="subtitle2" sx={{ color: TREND_COLORS.navy, fontWeight: 800, mb: 1 }}>
                      Varför det spelar roll
                    </Typography>
                    <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7 }}>
                      {selectedTrendCard.fontanaRelevance || selectedTrendCard.shortSummary}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderColor: TREND_COLORS.divider }}>
                    <Typography variant="subtitle2" sx={{ color: TREND_COLORS.navy, fontWeight: 800, mb: 1 }}>
                      Rekommenderad action
                    </Typography>
                    <Stack component="ul" spacing={0.75} sx={{ pl: 2, my: 0 }}>
                      {selectedTrendCard.recommendedActions.slice(0, 4).map((entry, index) => (
                        <Typography key={`${entry.department}-${index}`} component="li" variant="body2" sx={{ color: TREND_COLORS.textSecondary }}>
                          {entry.department}: {entry.action}
                        </Typography>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
              {(selectedTrendCard.validationNeeded.length > 0 || selectedTrendCard.uncertainties.length > 0) && (
                <Alert severity="warning">
                  {[...selectedTrendCard.validationNeeded, ...selectedTrendCard.uncertainties].slice(0, 4).join(' ')}
                </Alert>
              )}
              <SourceEvidencePanel
                refs={selectedTrendCard.sourceEvidenceRefs}
                collectedButUnusedCount={selectedTrendCard.collectedButUnusedSourceItemIds.length}
                title="Källor bakom trendkortet"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTrendCard(null)}>Stäng</Button>
        </DialogActions>
      </Dialog>

      <TrendStoryDetailDialog
        storyId={selectedStoryId}
        open={!!selectedStoryId}
        onClose={() => setSelectedStoryId(null)}
        qualityScope="all"
      />
      <TrendArticleDetailDialog
        articleId={selectedArticleId}
        open={!!selectedArticleId}
        onClose={() => setSelectedArticleId(null)}
      />
      <TrendOpportunityDetailDialog
        opportunity={selectedOpportunity}
        open={!!selectedOpportunity}
        onClose={() => setSelectedOpportunity(null)}
      />

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{getPickerLabel(pickerTarget)}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Sök..."
            value={pickerSearch}
            onChange={(event) => setPickerSearch(event.target.value)}
            sx={{ mb: 2, mt: 0.5 }}
          />
          {pickerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={28} sx={{ color: TREND_COLORS.navy }} />
            </Box>
          ) : (
            <Stack spacing={0.75} sx={{ maxHeight: 420, overflowY: 'auto' }}>
              {pickerEntries.length === 0 ? (
                <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted, fontStyle: 'italic' }}>
                  Inga träffar att visa.
                </Typography>
              ) : (
                pickerEntries.map((entry) => (
                  <Stack
                    key={entry.id}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    onClick={() => {
                      if (entry.disabled) return;
                      handlePickerSelect(entry.id);
                    }}
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      cursor: entry.disabled ? 'not-allowed' : 'pointer',
                      opacity: entry.disabled ? 0.6 : 1,
                      bgcolor: entry.disabled ? '#fafafa' : '#fff',
                      border: `1px solid ${TREND_COLORS.divider}`,
                      '&:hover': entry.disabled ? undefined : { bgcolor: TREND_COLORS.softNavy },
                    }}
                  >
                    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ color: TREND_COLORS.textPrimary, fontWeight: 600 }}>
                        {entry.title}
                      </Typography>
                      {entry.subtitle && (
                        <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }} noWrap>
                          {entry.subtitle}
                        </Typography>
                      )}
                    </Stack>
                    {entry.disabled && (
                      <Chip label="Redan vald" size="small" sx={{ fontSize: '0.55rem', height: 20, bgcolor: '#f2efe4' }} />
                    )}
                    {entry.chipLabel && (
                      <Chip label={entry.chipLabel} size="small" sx={{ fontSize: '0.55rem', height: 20 }} />
                    )}
                  </Stack>
                ))
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Stäng</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
